import cx from 'classnames';
import { t } from 'i18next';
import Numeral from 'numeral';
import { Component, ReactElement } from 'react';
import {
  formatMsFull,
  formatMsShortNoHours,
  naturalShortDate,
  relativeDateIfRecent
} from '../../../../../../shared/frontend/libs/utils';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { DIFFICULTIES } from '../../../../shared/enums';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import LoadableEncounterDetailsContent from '../container/loadableEncounterDetailsContent';
import { Link } from '../../../../../../shared/frontend/components/link';
import { SpecIcon } from '../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { ComplexTooltip, SimpleTooltip_NEXT } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { LoggedEntityStatus } from '../../../../../../shared/common/enums';
import { formatPhaseDisplay, getPhaseProgress } from '../../../../../../shared/common/bossPhases';

export function getPathToLoggedEncounter(encounter: any): string {
  return resolveRoutePath(namedRoutes.RaidLoggedEncounter, {
    raid: encounter.raid.slug,
    id: encounter.id
  });
}

function getBorderColorForDifficulty(diff: string) {
  if (diff === 'mythic') {
    return 'border-item-quality--5';
  } else if (diff === 'heroic') {
    return 'border-item-quality--3';
  } else {
    return 'border-item-quality--2';
  }
}

/**
 * RaidLoggedEncounterRow
 */
export class RaidLoggedEncounterRow extends Component<any, {}> {
  public render(): ReactElement<any> {
    const encounter = this.props.encounter;
    // responsible for showing the lock icon, only necessary in admin view(s)
    const usesPrivacy = this.props.usesPrivacy;

    const difficulty = DIFFICULTIES.find((d) => d.slug === encounter.raid.difficulty);

    const guild = encounter.guild || this.props.guild;

    const textStyles: string[] = [];
    if (encounter.is_reset || !guild) {
      textStyles.push('text-muted');
    }

    if (encounter.is_success && guild) {
      textStyles.push('text-raiderio text-bold');
    }

    let bossPercentCell = (
      <td className={cx('slds-text-align--right', ...textStyles)}>
        {guild && encounter.overall_percent !== null ? `${encounter.overall_percent.toFixed(2)}%` : '-'}
      </td>
    );

    if (encounter.status === LoggedEntityStatus.STARTED) {
      bossPercentCell = (
        <td className={cx('slds-text-align--right', ...textStyles)}>
          <Link to={getPathToLoggedEncounter(encounter)} className="rio-realm-link">
            <span className="text-muted">{t('encounter-details:status.unfinished')}</span>
          </Link>
        </td>
      );
    } else if (encounter.status === LoggedEntityStatus.ABORTED) {
      bossPercentCell = (
        <td className={cx('slds-text-align--right', ...textStyles)}>
          <Link to={getPathToLoggedEncounter(encounter)} className="rio-realm-link">
            <span className="text-muted">{t('encounter-details:status.abandoned')}</span>
          </Link>
        </td>
      );
    } else if (encounter.status === LoggedEntityStatus.FINISHED && encounter.overall_percent !== null) {
      const phaseProgress = getPhaseProgress(
        difficulty?.slug || '',
        encounter.encounter.slug,
        {
          overallPercent: encounter.overall_percent / 100,
          elapsedMs: encounter.duration_ms
        }
      );
      if (phaseProgress) {
        bossPercentCell = (
          <td className={cx('slds-text-align--right', ...textStyles)}>
            {formatPhaseDisplay(phaseProgress, (bossPercent) => `${(bossPercent * 100).toFixed(2)}%`)}
          </td>
        );
      }
    }

    let privacy = null;
    let isPrivate = false;

    // TODO: Can probably do this through localizations in future.
    // basically IGroupPrivacyValue
    type PrivacyLevel = 'all' | 'guild' | 'none';

    const privacyStringMap: Record<PrivacyLevel, string> = {
      all: 'encounter-details:privacy.public',
      guild: 'encounter-details:privacy.private-guild',
      none: 'encounter-details:privacy.private'
    };

    if (usesPrivacy) {
      privacy = encounter.guildPrivacy;

      if (
        privacy &&
        (privacy.share_live_raid_comps !== 'all' ||
          privacy.share_live_raid_pulls !== 'all' ||
          privacy.share_live_raid_percents !== 'all')
      ) {
        isPrivate = true;
      }
    }

    return (
      <>
        <tr
          key={encounter.id}
          className={'mythic-plus-rankings--row'}
          onClick={(event) => {
            if ((event.target as Element).closest?.('a')) {
              return;
            }

            if (!this.props.isOpen) {
              RIO_TRACK_EVENT('event', 'select_content', {
                content_type: 'raid_encounter',
                content_id: encounter.encounter.slug
              });
            }

            this.props.toggleOpen(encounter.id);
          }}
          style={{ cursor: 'pointer' }}
        >
          <td className="slds-text-align--center">
            <i className={cx('fa fa-fw', this.props.isOpen ? 'fa-caret-down' : 'fa-caret-right')} />
          </td>

          {this.props.columns.has('id') && (
            <td data-label={t('encounter-details:table.columns.rank')} className="slds-text-align--center text-muted slds-text-body--small">
              {encounter.id}
            </td>
          )}

          {this.props.columns.has('guild') && (
            <td data-label={t('encounter-details:table.columns.guild')}>
              {guild && (
                <>
                  {isPrivate && privacy && (
                    <ComplexTooltip
                      placement="top"
                      tag="span"
                      tooltip={
                        <div>
                          <p className="text-bold text-underline">{t('encounter-details:privacy.guild-has-privacy-enabled')}</p>
                          <p>{t('encounter-details:privacy.raid-pulls')} {t(privacyStringMap[privacy.share_live_raid_pulls as PrivacyLevel])}</p>
                          <p>{t('encounter-details:privacy.raid-percent')} {t(privacyStringMap[privacy.share_live_raid_percents as PrivacyLevel])}</p>
                          <p>{t('encounter-details:privacy.raid-comps')} {t(privacyStringMap[privacy.share_live_raid_comps as PrivacyLevel])}</p>
                        </div>
                      }
                    >
                      {/*
                       */}
                      <span
                        key={`guild-${guild.id}-${encounter.id}`}
                        className="slds-badge rio-badge rio-badge-size--small rio-border--light"
                        style={{ color: 'white' }}
                      >
                        <i className="slds-badge rio-badge fa-solid fa-lock" style={{ color: 'white' }} /> {t('encounter-details:privacy.private-label')}
                      </span>
                      {'  '}
                    </ComplexTooltip>
                  )}
                  <Link
                    className={`rio-faction-color--${guild.faction}`}
                    to={resolveRoutePath(namedRoutes.GuildDetails, {
                      region: guild.region.slug,
                      realm: guild.realm.slug,
                      guild: guild.name
                    })}
                  >
                    &lt;
                    {guild.name}
                    &gt;
                  </Link>
                </>
              )}
              {!guild && <span className="text-muted">-</span>}
            </td>
          )}

          {this.props.columns.has('boss') && (
            <td data-label={t('encounter-details:table.columns.boss-name')} className="slds-text-align--left">
              <span
                className={cx(
                  'slds-avatar slds-avatar--small rio-border--light slds-m-right--x-small',
                  getBorderColorForDifficulty(encounter.raid.difficulty)
                )}
              >
                <img src={asset(encounter.encounter.iconUrl)} />
              </span>
              <Link to={getPathToLoggedEncounter(encounter)} title={t('encounter-details:tooltips.link-to-encounter')} className="rio-realm-link">
                ({(difficulty ? difficulty.name : encounter.raid.difficulty).charAt(0)}) {encounter.encounter.name}
              </Link>
            </td>
          )}

          {this.props.columns.has('pull_number') && (
            <td className={cx('slds-text-align--center', ...textStyles)}>
              {encounter.is_reset ? '-' : (encounter.pull_count ?? '-')}
            </td>
          )}

          {this.props.columns.has('overall_percent') && bossPercentCell}

          {this.props.columns.has('duration') && (
            <td className="slds-text-align--center">
              {encounter.status === LoggedEntityStatus.STARTED && (
                <Link
                  to={getPathToLoggedEncounter(encounter)}
                  className={cx('rio-realm-link text-muted', ...textStyles)}
                >
                  {t('encounter-details:status.unfinished')}
                </Link>
              )}
              {encounter.status === LoggedEntityStatus.ABORTED && (
                <Link
                  to={getPathToLoggedEncounter(encounter)}
                  className={cx('rio-realm-link text-muted', ...textStyles)}
                >
                  {t('encounter-details:status.abandoned')}
                </Link>
              )}
              {encounter.status === 'finished' && (
                <>
                  <ComplexTooltip
                    placement="top"
                    tag="span"
                    tooltip={
                      <div className="slds-text-body--small">
                        <p>{relativeDateIfRecent(encounter.pull_ended_at)}</p>
                        <p>{naturalShortDate(encounter.pull_ended_at)}</p>
                        {encounter.is_success && (
                          <p style={{ color: '#66ff00' }}>
                            <b>{t('encounter-details:status.killed-in', { duration: formatMsFull(encounter.duration_ms) })}</b>
                          </p>
                        )}
                        {!encounter.is_success && (
                          <p style={{ color: '#ff6600' }}>
                            <b>{t('encounter-details:status.wiped-in', { duration: formatMsFull(encounter.duration_ms) })}</b>
                          </p>
                        )}
                      </div>
                    }
                  >
                    <Link to={getPathToLoggedEncounter(encounter)} className={cx('rio-realm-link', ...textStyles)}>
                      {formatMsShortNoHours(encounter.duration_ms)}
                    </Link>
                  </ComplexTooltip>
                </>
              )}
            </td>
          )}

          {this.props.columns.has('deaths') && (
            <td className={cx('slds-text-align--center', ...textStyles)}>{encounter.deaths}</td>
          )}

          {this.props.columns.has('item_level') && (
            <td>
              <ComplexTooltip
                placement="top"
                tooltip={
                  <p className="slds-text-align--center">
                    {t('encounter-details:item-level.minimum')} {Numeral(encounter.item_level.min).format('0.00')}
                    <br />
                    {t('encounter-details:item-level.maximum')} {Numeral(encounter.item_level.max).format('0.00')}
                    <br />
                    {t('encounter-details:item-level.average')} {Numeral(encounter.item_level.avg).format('0.00')}
                    <br />
                  </p>
                }
              >
                <Link
                  to={getPathToLoggedEncounter(encounter)}
                  title={t('encounter-details:tooltips.link-to-encounter')}
                  className={cx('rio-realm-link', ...textStyles)}
                >
                  {Numeral(encounter.item_level.avg).format('0.00')}
                </Link>
              </ComplexTooltip>
            </td>
          )}

          {this.props.columns.has('composition') &&
            (encounter.is_reset ? (
              <td data-label={t('encounter-details:table.columns.raid-composition')} className={cx('slds-text-align--left', ...textStyles)}>
                <i>{t('encounter-details:status.encounter-reset')}</i>
              </td>
            ) : (
              <td
                data-label={t('encounter-details:table.columns.raid-composition')}
                className={cx('slds-text-align--left', ...textStyles)}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {['tank', 'healer', 'dps-ranged', 'dps-melee'].map((role) => {
                  const specs = encounter.specComposition[role] || [];

                  const previousSpecs = this.props.previousEncounter?.specComposition[role] || [];

                  const previousSpecCounts = previousSpecs.reduce((accum: any, item: any) => {
                    if (!accum[item.spec.id]) {
                      accum[item.spec.id] = {
                        count: 0,
                        data: item
                      };
                    }
                    accum[item.spec.id].count++;
                    return accum;
                  }, {});

                  const specComponents = (
                    <>
                      {specs.map((spec: any, specIndex: number) => {
                        return (
                          <SimpleTooltip_NEXT
                            key={`${spec.id}_${specIndex}`}
                            tag="span"
                            placement="top"
                            tooltip={
                              <span className={`class-color--${spec.class.slug}`}>
                                {t(`game:specs.${spec.class.slug}.${spec.spec.slug}.name`)} {t(`game:classes.${spec.class.slug}.name`)}
                              </span>
                            }
                          >
                            <span
                              className={cx(
                                'slds-avatar slds-avatar--xx-small rio-border--light rio-shadow--small slds-m-right--xx-small',
                                typeof previousSpecCounts[spec.spec.id] === 'undefined' ||
                                  --previousSpecCounts[spec.spec.id].count < 0
                                  ? 'border-item-quality--6'
                                  : 'rio-spec-status--existing'
                              )}
                            >
                              <SpecIcon spec={spec.spec.slug} class={spec.class.slug} />
                            </span>
                          </SimpleTooltip_NEXT>
                        );
                      })}
                    </>
                  );

                  return (
                    <span key={role} className="slds-m-right--x-small slds-p-vertical--xx-small">
                      {specComponents}
                    </span>
                  );
                })}
              </td>
            ))}

          {this.props.columns.has('relative_date') && (
            <td
              className={cx('slds-text-align--right', ...textStyles)}
              style={{ width: 1 }}
              title={naturalShortDate(encounter.pull_started_at)}
            >
              {relativeDateIfRecent(encounter.pull_started_at)}
            </td>
          )}
        </tr>
        {this.props.isOpen && (
          <tr>
            <td colSpan={10} data-label={t('encounter-details:table.columns.group-composition')}>
              <LoadableEncounterDetailsContent
                loggedPage={true}
                raid={{
                  slug: encounter.raid.slug
                }}
                bossKillHeaderVariant={this.props.bossKillHeaderVariant}
                loggedEncounterId={encounter.id}
              />
            </td>
          </tr>
        )}
      </>
    );
  }
}
